import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
  StateMessageComponent,
} from '@groundworks/ngx-groundworks-ui';
import { ConfigService } from '../../config.service';
import { ProjectTypeConstants } from '../../constants/project-type.constants';
import { BoundProductInfo, ProjectCompletionErrorResponse } from '../../sdk/sdk';
import { ProjectService } from '../../services/project.service';

type CompletionState = 'idle' | 'loading' | 'success' | 'error';

const FALLBACK_ERROR_MESSAGE = 'Failed to complete project. Please try again.';

const FRIENDLY_409_MESSAGES: Record<string, string> = {
  ALREADY_COMPLETED: 'This project is already completed.',
  FUTURE_DATES: 'This project has install dates scheduled in the future. Reschedule or remove them before completing.',
  NO_ACTIVE_DATES: 'This project has no install dates. Add an install date before completing.',
  PROJECT_LOCKED: 'This project is cancelled or descoped and can no longer be completed.',
};

function extractErrorMessage(err: unknown): string {
  if (err instanceof ProjectCompletionErrorResponse && err.code) {
    return FRIENDLY_409_MESSAGES[err.code] ?? FALLBACK_ERROR_MESSAGE;
  }
  return FALLBACK_ERROR_MESSAGE;
}

@Component({
  selector: 'app-project-completion-modal',
  imports: [ModalComponent, ModalHeaderComponent, ModalBodyComponent, ModalFooterComponent, StateMessageComponent],
  templateUrl: './project-completion-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectCompletionModalComponent {
  readonly projectId = signal('');
  readonly projectTypeName = signal<string | undefined>(undefined);
  // Split & Complete context (MOD-3972): supplied only by the project card's completion flow.
  readonly projectTypeId = signal<string | undefined>(undefined);
  readonly boundProducts = signal<BoundProductInfo[]>([]);
  readonly state = signal<CompletionState>('idle');
  readonly errorMessage = signal(FALLBACK_ERROR_MESSAGE);

  private readonly activeModal = inject(NgbActiveModal);
  private readonly projectService = inject(ProjectService);
  private readonly configService = inject(ConfigService);
  private readonly destroyRef = inject(DestroyRef);

  readonly title = computed(() => {
    const name = this.projectTypeName();
    return name ? `Mark ${name} Project Complete` : 'Mark Project Complete';
  });

  readonly loading = computed(() => this.state() === 'loading');
  readonly canDismiss = computed(() => this.state() !== 'loading');

  // Splitting needs ≥2 total units so both sides can keep the 1-unit minimum. The signals are
  // read before the plain config flag so the computed always tracks them — a leading falsy
  // flag would short-circuit past every signal read and pin the first evaluation forever.
  readonly splitEligible = computed(() =>
    this.projectTypeId() === ProjectTypeConstants.Commercial &&
    this.boundProducts().reduce((sum, bp) => sum + (bp.quantity ?? 0), 0) >= 2 &&
    this.configService.enableProjectSplit);

  readonly footerActions = computed<ModalFooterAction[]>(() => {
    switch (this.state()) {
      case 'success':
        return [];
      case 'error':
        return [
          { label: 'Close', type: 'outline-secondary', action: () => this.cancel() },
        ];
      default:
        return this.splitEligible() ? this.buildSplitAndCompleteActions() : this.buildStandardActions();
    }
  });

  // AC1 (MOD-3972): commercial completions offer three options — "No, Go Back" on the left,
  // "Complete Project" and "Split & Complete" on the right.
  private buildSplitAndCompleteActions(): ModalFooterAction[] {
    return [
      {
        label: 'No, Go Back',
        type: 'outline-secondary',
        action: () => this.cancel(),
        disabled: () => this.loading(),
      },
      {
        label: 'Complete Project',
        type: 'secondary',
        action: () => this.confirm(),
        loading: () => this.loading(),
        disabled: () => this.loading(),
      },
      {
        label: 'Split & Complete',
        type: 'primary',
        action: () => this.splitAndComplete(),
        disabled: () => this.loading(),
      },
    ];
  }

  private buildStandardActions(): ModalFooterAction[] {
    return [
      {
        label: 'No, Go Back',
        type: 'outline-secondary',
        action: () => this.cancel(),
        disabled: () => this.loading(),
      },
      {
        label: 'Yes, Complete Project',
        type: 'primary',
        action: () => this.confirm(),
        loading: () => this.loading(),
        disabled: () => this.loading(),
      },
    ];
  }

  // AC2: hand control back to the opener, which owns the split modal and its data. The
  // completion itself happens server-side as part of the split (Split & Complete).
  splitAndComplete(): void {
    if (this.loading()) return;
    this.activeModal.close('split');
  }

  confirm(): void {
    if (this.loading()) return;
    this.errorMessage.set(FALLBACK_ERROR_MESSAGE);
    this.state.set('loading');
    this.projectService.completeProject(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.state.set('success'),
        error: (err: unknown) => {
          this.errorMessage.set(extractErrorMessage(err));
          this.state.set('error');
        },
      });
  }

  cancel(): void {
    if (!this.canDismiss()) return;
    if (this.state() === 'success') {
      this.activeModal.close(true);
    } else {
      this.activeModal.dismiss(false);
    }
  }
}
